/**
 * @module components/ConditionalFormat
 * @description 表格条件格式配置（交互对齐 ChartsDrop ConditionalFormat）
 *
 * 状态管理模式：
 *   读/写: useSettingObjectState('conditional_format')
 *
 * QB 展示名联动：
 *   摘要指标名来自 `getMetricName` → 当前 QB 列表的 `column_display`。
 *   须用 `useQueryColumnMetrics` / `useQueryColumnRawColumns` 订阅（非默认 shallowEqual），
 *   否则 updateTableField 原地 mutate 后摘要不刷新。见 hooks/dva/useQueryColumnLists.ts。
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Button, Modal, Segment } from 'tea-component';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import { useSettingObjectState } from '../../context';
import {
  useQueryColumnDimensions,
  useQueryColumnMetrics,
  useQueryColumnRawColumns,
  useQuerySelector,
  useResultSelector,
} from '../../hooks/deps';
import { HorizontalBar } from './HorizontalBar';
import ConditionalFormatListItem from './ConditionalFormatListItem';
import NumericalValues from './NumericalValues';
import type {
  IConditionalFormatValue,
  IMetricOption,
  IBarPercentageItem,
  INumericalValueItem,
  IDimensionOption,
  TSegmentValue,
  ITableWithBarPercentage,
} from './type';
import './index.scss';

const ConditionalFormat: React.FC = () => {
  const { formatMessage } = useIntl();
  const { value: initValue, setValue: persistConditionalFormat } =
    useSettingObjectState('conditional_format');
  // 展示名重命名：model 先 mutate 再 put 新数组引用，须用 useQueryColumn*（见 useQueryColumnLists.ts）
  const columnDimensions = useQueryColumnDimensions();
  const columnMetrics = useQueryColumnMetrics();
  const mode = useQuerySelector(s => s.mode as TChartCalculateMode);
  const columnRawColumns = useQueryColumnRawColumns();
  const table = useResultSelector(
    s => s.table as ITableWithBarPercentage | undefined,
  );

  const isRawColumn = mode === ChartCalculateMode.RawColumn;

  const dimensions = useMemo((): IDimensionOption[] => {
    if (isRawColumn) {
      return [];
    }
    return (columnDimensions || []) as IDimensionOption[];
  }, [isRawColumn, columnDimensions]);

  const metrics = useMemo((): IMetricOption[] => {
    const source = isRawColumn ? columnRawColumns : columnMetrics;
    return ((source || []) as IColumnInfo[]).filter(
      item => item?.is_metric || item?.column_custom,
    ) as IMetricOption[];
  }, [isRawColumn, columnRawColumns, columnMetrics]);

  const [visible, setVisible] = useState(false);
  const [conditionValue, setConditionValue] = useState<IConditionalFormatValue>(initValue);
  const [segmentValue, setSegmentValue] = useState<TSegmentValue>('bar_chart');

  const onChange = useCallback((value: Partial<IConditionalFormatValue>) => {
    setConditionValue(prev => ({ ...prev, ...value }));
  }, []);

  useEffect(() => {
    if (visible) {
      setConditionValue(initValue);
      setSegmentValue('bar_chart');
    }
  }, [initValue, visible]);

  // 操作区指标修改联动条件格式（对齐旧 ChartsDrop）
  useEffect(() => {
    const metricsIdList = metrics?.map((item: IMetricOption) => item?.id);
    const barList = conditionValue?.table_bar_percentage || [];
    const numericalList = conditionValue?.table_numerical_values || [];
    const hasStaleBar = barList.some(
      (item: { id: string }) => !metricsIdList?.includes(item?.id),
    );
    const hasStaleNumerical = numericalList.some(
      (item: { id: string }) => !metricsIdList?.includes(item?.id),
    );
    if (!hasStaleBar && !hasStaleNumerical) {
      return;
    }
    const nextBarList = hasStaleBar
      ? barList.filter((item: { id: string }) =>
          metricsIdList?.includes(item?.id),
        )
      : barList;
    const nextNumericalList = hasStaleNumerical
      ? numericalList.filter((item: { id: string }) =>
          metricsIdList?.includes(item?.id),
        )
      : numericalList;
    const next: IConditionalFormatValue = {
      ...conditionValue,
      table_bar_percentage: nextBarList,
      table_numerical_values: nextNumericalList,
    };
    setConditionValue(next);
    persistConditionalFormat({
      table_bar_percentage: nextBarList,
      table_numerical_values: nextNumericalList,
    });
  }, [JSON.stringify(metrics), JSON.stringify(initValue)]);

  const filterFormatLists = useCallback(
    (source?: IConditionalFormatValue) => {
      const filterBarList = source?.table_bar_percentage?.filter(
        (item: { id: string }) =>
          metrics?.some((v: IMetricOption) => v?.id === item?.id),
      );
      const filterNumericalList = source?.table_numerical_values?.filter(
        (item: { id: string }) =>
          metrics?.some((v: IMetricOption) => v?.id === item?.id),
      );
      return { filterBarList, filterNumericalList };
    },
    [metrics],
  );

  const {
    filterBarList: savedFilterBarList,
    filterNumericalList: savedFilterNumericalList,
  } = useMemo(() => filterFormatLists(initValue), [filterFormatLists, initValue]);

  const showList =
    (savedFilterBarList?.length || 0) > 0 ||
    (savedFilterNumericalList?.length || 0) > 0;

  const {
    filterBarList: modalFilterBarList,
    filterNumericalList: modalFilterNumericalList,
  } = useMemo(
    () => filterFormatLists(conditionValue),
    [filterFormatLists, conditionValue],
  );

  const segmentOptions = useMemo(
    () => [
      {
        value: 'bar_chart',
        text: formatMessage(
          { id: 'dbl_chart_condition_bar_chart_count' },
          { count: modalFilterBarList?.length || 0 },
        ),
      },
      {
        value: 'numerical_values',
        text: formatMessage(
          { id: 'dbl_chart_condition_numerical_values' },
          { count: modalFilterNumericalList?.length || 0 },
        ),
      },
    ],
    [
      modalFilterBarList?.length,
      modalFilterNumericalList?.length,
      formatMessage,
    ],
  );

  const getMetricName = useCallback(
    (id: string, fallback?: string) => {
      const metric = metrics?.find((v: IMetricOption) => v?.id === id);
      return metric?.column_display || metric?.column_name || fallback;
    },
    [metrics],
  );

  const barTypeLabel = formatMessage({ id: 'dbl_chart_condition_bar_chart' });
  const numericalTypeLabel = formatMessage({
    id: 'dbl_chart_condition_numerical',
  });

  const handleDeleteBarItem = useCallback(
    (id: string) => {
      persistConditionalFormat({
        ...initValue,
        table_bar_percentage:
          initValue?.table_bar_percentage?.filter(
            (v: { id: string }) => v?.id !== id,
          ) || [],
      });
    },
    [initValue, persistConditionalFormat],
  );

  const handleDeleteNumericalItem = useCallback(
    (id: string) => {
      persistConditionalFormat({
        ...initValue,
        table_numerical_values:
          initValue?.table_numerical_values?.filter(
            (v: { id: string }) => v?.id !== id,
          ) || [],
      });
    },
    [initValue, persistConditionalFormat],
  );

  const handleConfirm = useCallback(() => {
    persistConditionalFormat(conditionValue);
    setVisible(false);
  }, [conditionValue, persistConditionalFormat]);

  const handleCancel = useCallback(() => {
    setConditionValue(initValue);
    setVisible(false);
  }, [initValue]);

  return (
    <>
      <div className="dbl-chart-conditional-format">
        <div className="dbl-config-edit-head">
          <div>{formatMessage({ id: 'dbl_chart_conditional_formatting' })}</div>
          <div className="dbl-link-style" onClick={() => setVisible(true)}>
            {formatMessage({ id: 'dbl_common_edit' })}
          </div>
        </div>
        {showList && (
          <div className="dbl-chart-conditional-format-list">
            {savedFilterBarList?.map((item: IBarPercentageItem, index) => (
              <ConditionalFormatListItem
                key={item.id}
                index={index + 1}
                typeLabel={barTypeLabel}
                metricName={getMetricName(item.id, item.metric)}
                onDelete={() => handleDeleteBarItem(item.id)}
              />
            ))}
            {savedFilterNumericalList?.map(
              (item: INumericalValueItem, index) => (
                <ConditionalFormatListItem
                  key={item.id}
                  index={(savedFilterBarList?.length || 0) + index + 1}
                  typeLabel={numericalTypeLabel}
                  metricName={getMetricName(
                    item.id,
                    item.metric,
                  )}
                  onDelete={() => handleDeleteNumericalItem(item.id)}
                />
              ),
            )}
          </div>
        )}
      </div>
      <Modal
        visible={visible}
        caption={formatMessage({
          id: 'dbl_chart_condition_batch_config_condition_format',
        })}
        onClose={() => setVisible(false)}
        className={classNames('dbl-chart-condition-modal', {
          'dbl-chart-condition-modal--numerical-values':
            segmentValue === 'numerical_values',
        })}
      >
        <Modal.Body>
          <Segment
            className="dbl-chart-condition-segment"
            value={segmentValue}
            onChange={(value: TSegmentValue | string) => {
              const next: TSegmentValue =
                value === 'bar_chart' || value === 'numerical_values'
                  ? value
                  : 'bar_chart';
              setSegmentValue(next);
            }}
            options={segmentOptions}
          />
          {segmentValue === 'bar_chart' && (
            <HorizontalBar
              value={conditionValue?.table_bar_percentage}
              onChange={onChange}
              dimensions={dimensions}
              metrics={metrics}
              table={table}
            />
          )}
          {segmentValue === 'numerical_values' && (
            <NumericalValues
              value={conditionValue?.table_numerical_values}
              onChange={onChange}
              dimensions={dimensions}
              metrics={metrics}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="primary" onClick={handleConfirm}>
            {formatMessage({ id: 'dbl_common_confirm' })}
          </Button>
          <Button type="weak" onClick={handleCancel}>
            {formatMessage({ id: 'dbl_common_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default React.memo(ConditionalFormat);
